// src/UsersList.js
import React, { useEffect, useMemo, useState } from "react";
import { api, API_BASE } from "./api";

/**
 * UsersManager (RBAC / Permissions - REPLACE mode)
 *
 * Endpoints expected on server.php:
 *  GET    /server.php/users
 *  POST   /server.php/users
 *  PUT    /server.php/users/{id}/active
 *  PUT    /server.php/users/{id}/password
 *  PUT    /server.php/users/{id}/permissions   <-- NEW
 *
 * Server responses expected:
 *  { success: true|false, message?: string, data?: any }
 */

const ALL_PERMISSIONS = [
  { code: "users.manage", label: "Create/Manage Users + Permissions" },
  { code: "inventory.view", label: "View Inventory" },
  { code: "inventory.manage", label: "Create/Update Inventory" },
  { code: "pos.sell", label: "Use POS / Sell" },
  { code: "reports.view", label: "View Reports" },
  { code: "reports.print", label: "Print Reports" },
  // super_user only (kept here for display, but you should not assign it from Admin UI)
  { code: "store.create", label: "Register/Create Stores (Super User)" },
];

function getSessionUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function can(permissionCode) {
  const u = getSessionUser();
  if (!u) return false;
  if (u.role === "super_user") return true;
  const perms = Array.isArray(u.permissions) ? u.permissions : [];
  return perms.includes(permissionCode);
}

export default function UsersManager({ onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [q, setQ] = useState("");

  // Add form
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("cashier");

  // ✅ REPLACE permissions: admin picks exact perms
  const [newPerms, setNewPerms] = useState(["inventory.view", "pos.sell"]);

  // Reset password UI
  const [pwUserId, setPwUserId] = useState(null);
  const [pwNew, setPwNew] = useState("");

  // Edit permissions panel
  const [editing, setEditing] = useState(null); // user row
  const [editPerms, setEditPerms] = useState([]);

  const forbidden = !can("users.manage");

  const showMsg = (m) => {
    setMsg(m);
    window.clearTimeout(showMsg._t);
    showMsg._t = window.setTimeout(() => setMsg(""), 4500);
  };

  const togglePerm = (arr, code) => {
    if (arr.includes(code)) return arr.filter((x) => x !== code);
    return [...arr, code];
  };

  async function loadUsers() {
    setLoading(true);
    try {
      const res = await api.get("server.php/users");
      const arr = Array.isArray(res.data?.data) ? res.data.data : [];
      setUsers(arr);
    } catch (e) {
      console.error(e);
      showMsg("❌ Failed to load users. Check GET server.php/users, X-Store-ID, and users.manage permission.");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!forbidden) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) => {
      const perms = Array.isArray(u.permissions) ? u.permissions.join(",") : "";
      return (
        String(u.id).includes(s) ||
        (u.full_name || "").toLowerCase().includes(s) ||
        (u.email || "").toLowerCase().includes(s) ||
        (u.phone || "").toLowerCase().includes(s) ||
        (u.username || "").toLowerCase().includes(s) ||
        (u.role || "").toLowerCase().includes(s) ||
        perms.toLowerCase().includes(s)
      );
    });
  }, [users, q]);

  async function addUser(e) {
    e.preventDefault();

    if (!fullName.trim()) return showMsg("⚠️ Full name required");
    if (!username.trim()) return showMsg("⚠️ Username required");

    if (!password.trim() || password.length < 6) {
      return showMsg("⚠️ Password must be at least 6 characters");
    }

    // In replace mode, allow empty perms if you really want,
    // but typically at least pos.sell or inventory.view makes sense.
    if (!Array.isArray(newPerms)) return showMsg("⚠️ Permissions invalid");

    setLoading(true);
    try {
      const payload = {
        full_name: fullName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        username: username.trim(),
        password: password,
        role: role || "cashier",
        is_active: 1,
        permissions: newPerms.filter((p) => p !== "store.create"), // do not assign store.create from admin UI
      };

      const res = await api.post("server.php/users", payload);

      if (res.data?.success) {
        showMsg("✅ " + (res.data?.message || "User created"));
        setFullName("");
        setEmail("");
        setPhone("");
        setUsername("");
        setPassword("");
        setRole("cashier");
        setNewPerms(["inventory.view", "pos.sell"]);
        await loadUsers();
      } else {
        showMsg("❌ " + (res.data?.message || "Failed to create user"));
      }
    } catch (e) {
      console.error(e);
      const serverMsg = e?.response?.data?.message;
      const status = e?.response?.status;
      showMsg(
        "❌ " +
          (serverMsg ||
            (status === 409
              ? "Username already exists."
              : "Failed to create user. Check X-Store-ID and server.php users endpoint.")),
      );
    } finally {
      setLoading(false);
    }
  }

  async function toggleActive(id, nextActive) {
    setLoading(true);
    try {
      const res = await api.put(`server.php/users/${id}/active`, {
        is_active: nextActive ? 1 : 0,
      });

      if (res.data?.success) {
        setUsers((prev) =>
          prev.map((u) =>
            u.id === id ? { ...u, is_active: nextActive ? 1 : 0 } : u
          )
        );
        showMsg("✅ " + (res.data?.message || "Updated status"));
      } else {
        showMsg("❌ " + (res.data?.message || "Failed to update status"));
      }
    } catch (e) {
      console.error(e);
      showMsg("❌ Failed to update status (check PUT /users/{id}/active)");
    } finally {
      setLoading(false);
    }
  }

  async function resetPassword(id) {
    if (!pwNew || pwNew.length < 6) return showMsg("⚠️ New password min 6 chars");

    setLoading(true);
    try {
      const res = await api.put(`server.php/users/${id}/password`, {
        password: pwNew,
      });

      if (res.data?.success) {
        showMsg("✅ " + (res.data?.message || "Password updated"));
        setPwUserId(null);
        setPwNew("");
      } else {
        showMsg("❌ " + (res.data?.message || "Failed to update password"));
      }
    } catch (e) {
      console.error(e);
      showMsg("❌ Failed to update password (check PUT /users/{id}/password)");
    } finally {
      setLoading(false);
    }
  }

  const openEdit = (u) => {
    setEditing(u);
    const perms = Array.isArray(u.permissions) ? u.permissions : [];
    setEditPerms(perms.filter((p) => p !== "store.create")); // admin cannot grant store.create
  };

  async function savePermissions() {
    if (!editing) return;

    setLoading(true);
    try {
      const res = await api.put(`server.php/users/${editing.id}/permissions`, {
        permissions: editPerms.filter((p) => p !== "store.create"),
      });

      if (res.data?.success) {
        showMsg("✅ " + (res.data?.message || "Permissions updated"));
        setEditing(null);
        setEditPerms([]);
        await loadUsers();
      } else {
        showMsg("❌ " + (res.data?.message || "Failed to update permissions"));
      }
    } catch (e) {
      console.error(e);
      showMsg("❌ Failed to update permissions (check PUT /users/{id}/permissions)");
    } finally {
      setLoading(false);
    }
  }

  if (forbidden) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Users</div>
            <div style={styles.sub}>
              API: <code>{API_BASE}</code>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            {onBack ? (
              <button style={styles.btn} onClick={onBack}>
                ⬅ Back
              </button>
            ) : null}
          </div>
        </div>

        <div style={styles.msg}>
          <b>Forbidden</b>
          <div style={{ marginTop: 6 }}>
            Your account does not have <code>users.manage</code>.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Users</div>
          <div style={styles.sub}>
            API: <code>{API_BASE}</code> → <code>server.php/users</code>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.btn} onClick={loadUsers} disabled={loading}>
            🔄 Refresh
          </button>
          {onBack ? (
            <button style={styles.btn} onClick={onBack} disabled={loading}>
              ⬅ Back
            </button>
          ) : null}
        </div>
      </div>

      {msg ? <div style={styles.msg}>{msg}</div> : null}

      <div style={styles.grid}>
        {/* Add user */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Add User</div>
          <form onSubmit={addUser} style={{ display: "grid", gap: 10 }}>
            <label style={styles.label}>
              Full Name
              <input
                style={styles.input}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="e.g. Kevin Kezengwa"
              />
            </label>

            <label style={styles.label}>
              Email (optional)
              <input
                style={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </label>

            <label style={styles.label}>
              Phone (optional)
              <input
                style={styles.input}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="437-xxx-xxxx"
              />
            </label>

            <label style={styles.label}>
              Username
              <input
                style={styles.input}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. cashier1"
              />
            </label>

            <label style={styles.label}>
              Password (min 6)
              <input
                style={styles.input}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="min 6 chars"
              />
            </label>

            <label style={styles.label}>
              Role (label only; access is driven by permissions)
              <select
                style={styles.input}
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="cashier">cashier</option>
                <option value="manager">manager</option>
                <option value="admin">admin</option>
              </select>
            </label>

            <div style={{ display: "grid", gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 800 }}>Permissions (REPLACE)</div>
              <div style={styles.permGrid}>
                {ALL_PERMISSIONS.filter((p) => p.code !== "store.create").map((p) => (
                  <label key={p.code} style={styles.permItem}>
                    <input
                      type="checkbox"
                      checked={newPerms.includes(p.code)}
                      onChange={() => setNewPerms((prev) => togglePerm(prev, p.code))}
                    />
                    <span>
                      <code>{p.code}</code> — {p.label}
                    </span>
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75 }}>
                Whatever you tick becomes the user’s exact access list.
              </div>
            </div>

            <button style={styles.primaryBtn} disabled={loading}>
              ➕ Create User
            </button>
          </form>
        </div>

        {/* View users */}
        <div style={styles.card}>
          <div style={styles.rowBetween}>
            <div style={styles.cardTitle}>View Users</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                style={{ ...styles.input, width: 320 }}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name, username, role, permission..."
              />
              <span style={styles.pill}>
                {loading ? "Loading..." : `${filtered.length} users`}
              </span>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>ID</th>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Username</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Active</th>
                  <th style={styles.th}>Permissions</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id}>
                    <td style={styles.td}>{u.id}</td>
                    <td style={styles.td}>{u.full_name || "-"}</td>
                    <td style={styles.td}>{u.username || "-"}</td>
                    <td style={styles.td}>{u.role || "-"}</td>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={Number(u.is_active) === 1}
                        onChange={(e) => toggleActive(u.id, e.target.checked)}
                        disabled={loading}
                      />
                    </td>

                    <td style={styles.td}>
                      {Array.isArray(u.permissions) && u.permissions.length ? (
                        <div style={styles.badges}>
                          {u.permissions.map((p) => (
                            <span key={p} style={styles.badge}>{p}</span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ opacity: 0.7 }}>—</span>
                      )}
                    </td>

                    <td style={styles.td}>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          style={styles.smallBtn}
                          onClick={() => openEdit(u)}
                          disabled={loading}
                          type="button"
                        >
                          🔧 Permissions
                        </button>

                        {pwUserId === u.id ? (
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <input
                              style={{ ...styles.input, width: 170 }}
                              type="password"
                              value={pwNew}
                              onChange={(e) => setPwNew(e.target.value)}
                              placeholder="new password (min 6)"
                            />
                            <button
                              style={styles.smallPrimary}
                              onClick={() => resetPassword(u.id)}
                              disabled={loading}
                              type="button"
                            >
                              Save
                            </button>
                            <button
                              style={styles.smallBtn}
                              onClick={() => {
                                setPwUserId(null);
                                setPwNew("");
                              }}
                              disabled={loading}
                              type="button"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            style={styles.smallBtn}
                            onClick={() => setPwUserId(u.id)}
                            disabled={loading}
                            type="button"
                          >
                            🔑 Reset Password
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

                {!loading && filtered.length === 0 ? (
                  <tr>
                    <td style={styles.td} colSpan={7}>
                      No users found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* Edit panel */}
          {editing ? (
            <div style={{ marginTop: 12, padding: 12, border: "1px solid #eee", borderRadius: 12, background: "#fafafa" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                <div style={{ fontWeight: 900 }}>
                  Edit permissions (REPLACE): <code>{editing.username}</code>
                </div>
                <button style={styles.smallBtn} onClick={() => { setEditing(null); setEditPerms([]); }} disabled={loading}>
                  ✖ Close
                </button>
              </div>

              <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                <div style={styles.permGrid}>
                  {ALL_PERMISSIONS.filter((p) => p.code !== "store.create").map((p) => (
                    <label key={p.code} style={styles.permItem}>
                      <input
                        type="checkbox"
                        checked={editPerms.includes(p.code)}
                        onChange={() => setEditPerms((prev) => togglePerm(prev, p.code))}
                      />
                      <span>
                        <code>{p.code}</code> — {p.label}
                      </span>
                    </label>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button style={styles.smallPrimary} onClick={savePermissions} disabled={loading} type="button">
                    💾 Save
                  </button>
                  <button style={styles.smallBtn} onClick={() => { setEditing(null); setEditPerms([]); }} disabled={loading} type="button">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: 16,
    fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    marginBottom: 12,
  },
  title: { fontSize: 22, fontWeight: 800 },
  sub: { fontSize: 12, opacity: 0.75 },
  msg: {
    marginBottom: 12,
    padding: "10px 12px",
    border: "1px solid #ddd",
    borderRadius: 10,
    background: "#fafafa",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "360px 1fr",
    gap: 14,
    alignItems: "start",
  },
  card: {
    border: "1px solid #e5e5e5",
    borderRadius: 14,
    padding: 14,
    background: "white",
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
  },
  cardTitle: { fontSize: 16, fontWeight: 800, marginBottom: 10 },
  rowBetween: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    marginBottom: 8,
  },
  label: { display: "grid", gap: 6, fontSize: 13 },
  input: {
    border: "1px solid #d9d9d9",
    borderRadius: 10,
    padding: "10px 10px",
    outline: "none",
    fontSize: 14,
    background: "white",
  },
  btn: {
    border: "1px solid #d9d9d9",
    borderRadius: 10,
    padding: "10px 12px",
    background: "white",
    cursor: "pointer",
  },
  primaryBtn: {
    border: "1px solid #0b5",
    borderRadius: 10,
    padding: "10px 12px",
    background: "#0b5",
    color: "white",
    fontWeight: 700,
    cursor: "pointer",
  },
  pill: {
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #e5e5e5",
    fontSize: 12,
    background: "#fafafa",
    whiteSpace: "nowrap",
  },
  tableWrap: { overflow: "auto", border: "1px solid #eee", borderRadius: 12 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: 10, borderBottom: "1px solid #eee", background: "#fafafa" },
  td: { padding: 10, borderBottom: "1px solid #f0f0f0", verticalAlign: "top" },
  smallBtn: {
    border: "1px solid #d9d9d9",
    borderRadius: 10,
    padding: "8px 10px",
    background: "white",
    cursor: "pointer",
    fontSize: 12,
  },
  smallPrimary: {
    border: "1px solid #0b5",
    borderRadius: 10,
    padding: "8px 10px",
    background: "#0b5",
    color: "white",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
  },
  permGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 6,
    border: "1px solid #eee",
    borderRadius: 12,
    padding: 10,
    background: "#fff",
  },
  permItem: { display: "flex", gap: 10, alignItems: "center", fontSize: 13 },
  badges: { display: "flex", flexWrap: "wrap", gap: 6 },
  badge: {
    border: "1px solid #e5e5e5",
    borderRadius: 999,
    padding: "2px 8px",
    fontSize: 12,
    background: "#fafafa",
  },
};